import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  applyPercentDiscount,
  FRANCHISEE_DISCOUNT_PERCENT,
  formatPrice,
  parsePriceToCents,
  type Currency,
} from "@nh/shared";
import {
  DerivedValue,
  FormGrid,
  FormRow,
  MoneyField,
} from "@/features/admin/components";
import type { CurrencyRowValues, ProductFormValues } from "../productForm";

/**
 * Cómo se llama cada mercado en pantalla. El dato sigue siendo el código
 * ISO —es la llave de todos los mapas de dinero—, pero el rótulo dice el
 * país, que es como Anahí nombra las filas: "México y Estados Unidos".
 */
const COUNTRY_NAMES: Record<string, string> = {
  MXN: "México",
  USD: "Estados Unidos",
};

/** Lo tecleado ("1299.50") en centavos, o `undefined` si no se entiende. */
function readCents(raw: unknown): number | undefined {
  const trimmed = (typeof raw === "string" ? raw : "").trim();
  if (trimmed === "") return undefined;
  const cents = parsePriceToCents(trimmed);
  return cents === null ? undefined : cents;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Los TRES precios de un país, en una fila.
 *
 *   Público          → se escribe.
 *   Socio            → se escribe.
 *   Franquiciatario  → se CALCULA, del porcentaje del propio paso.
 *
 * ── Por qué el de SOCIO sí se escribe ───────────────────────────────
 * Porque no es un descuento: es un importe negociado por producto y por
 * moneda. Los números del catálogo real lo demuestran solos — EVA Plus
 * va de 1,470.00 a 1,250.00 en pesos (14.97 %) y de 74.00 a 63.00 en
 * dólares (14.86 %) —: no hay un porcentaje que produzca los dos.
 *
 * ── Y por qué el de FRANQUICIATARIO no ──────────────────────────────
 * Sale de `franchiseeDiscountPercent` sobre el precio de socio
 * (confirmado por Anahí). Escribirlo como importe sería una segunda
 * fuente para la misma rebaja, y las dos se contradicen en cuanto
 * alguien toca una. `DerivedValue` lo pinta de solo lectura y dice
 * sobre qué se calculó.
 * ═══════════════════════════════════════════════════════════════════
 */
function CurrencyRow({ code }: { code: string }) {
  const { t } = useTranslation();

  const publico = readCents(useWatch({ name: `prices.${code}` }));
  const socio = readCents(useWatch({ name: `memberPrices.${code}` }));
  // Fijo en todo el sistema (Anahí, 30-sep-2026): ya no se captura por producto.
  const franchiseePercent = FRANCHISEE_DISCOUNT_PERCENT;

  const baseFranquicia = socio ?? publico;
  const franquiciatario =
    baseFranquicia !== undefined && franchiseePercent !== undefined
      ? applyPercentDiscount(baseFranquicia, franchiseePercent)
      : undefined;

  const country = COUNTRY_NAMES[code];

  return (
    <li className="rounded-2xl border border-ds-border bg-ds-cream-2/40 p-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-ds-label-wide text-ds-ink-3">
        {country ? `${t(country)} · ${code}` : code}
      </p>

      <FormGrid columns={3}>
        <MoneyField
          name={`prices.${code}`}
          label={`${t("Precio público")} ${code}`}
          currency={code}
        />
        <MoneyField
          name={`memberPrices.${code}`}
          label={`${t("Precio de socio")} ${code}`}
          currency={code}
        />
        <DerivedValue
          compact
          label={`${t("Precio franquiciatario")} ${code}`}
          value={
            franquiciatario === undefined
              ? "—"
              : formatPrice(franquiciatario, code as Currency)
          }
          note={
            franchiseePercent === undefined
              ? t("Pon el descuento de franquiciatario arriba.")
              : socio !== undefined
                ? t("{{percent}}% sobre el precio de socio", {
                    percent: franchiseePercent,
                  })
                : t("{{percent}}% sobre el precio público", {
                    percent: franchiseePercent,
                  })
          }
        />
      </FormGrid>
    </li>
  );
}

/**
 * Los precios por país: México y Estados Unidos.
 *
 * ── Las filas se LEEN del formulario, pero ya no se editan ──────────
 * "Solo es Estados Unidos y México" (Anahí, 26-ago): fuera el campo de
 * código, el botón de añadir moneda y el de quitar. Las filas siguen
 * saliendo de `currencies` —la lista de monedas activas del producto—
 * y no de una constante, para que un producto guardado con OTRA moneda
 * no pierda sus importes en silencio: su fila aparece igual y se puede
 * corregir. Un producto nuevo nace con las dos de siempre
 * (`DEFAULT_CURRENCIES`).
 *
 * El porcentaje de franquiciatario vive ARRIBA de las filas: es el
 * control del que sale el tercer precio de cada país, y cada tecla
 * cambia esos renglones a la vista.
 */
export function ProductCurrencyRows() {
  const { t } = useTranslation();
  const { watch } = useFormContext<ProductFormValues>();
  const rows = (watch("currencies") ?? []) as CurrencyRowValues[];

  const codes = rows
    .map((row) => (row?.code ?? "").trim().toUpperCase())
    .filter((code) => /^[A-Z]{3}$/.test(code));

  return (
    <FormRow className="gap-3">
      {/*
        El descuento de franquiciatario es FIJO: 10 % sobre el precio de
        socio en todo el sistema (Anahí, 30-sep-2026). Ya no es un campo:
        se dice, y cada fila muestra el precio que resulta.
      */}
      <p className="text-[13px] text-ds-ink-3">
        {t("Franquiciatarios: {{pct}} % menos sobre el precio de socio, igual en todos los productos.", {
          pct: FRANCHISEE_DISCOUNT_PERCENT,
        })}
      </p>

      <ol className="grid gap-3">
        {codes.map((code) => (
          <CurrencyRow key={code} code={code} />
        ))}
      </ol>
    </FormRow>
  );
}
